import { getByID } from '@adornis/baseql/operations/mongo.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { format } from 'date-fns';
import { html, nothing } from 'lit';
import { Case } from '../../db/Case.js';

export const TicketDetail = stateful(({ ticketID }: { ticketID: string }) => {
  const ticketQuery = useQuery(
    () =>
      getByID(
        Case,
        ticketID,
      )({
        caseNumber: 1,
        phoneNumber: 1,
        description: 1,
        createdOn: 1,
        contact: 1,
        knowledgeQueue: 1,
        vapiCallLog: 1,
        escalated: 1,
        escalatedOn: 1,
        status: 1,
        priority: 1,
        machineResolved: {
          machineName: 1,
          serialNumber: 1,
          machineType: 1,
          dossierNumber: 1,
          manufacturer: 1,
          productGroup: 1,
        },
        accountResolved: {
          accountName: 1,
          accountNumber: 1,
          address1City: 1,
          address1Street1: 1,
          address1ZipPostalCode: 1,
          address1Country: 1,
          industry: 1,
        },
      }),
    [ticketID],
  );

  const loading = ticketQuery.loading;
  const ticket = ticketQuery.value;
  const machine = ticket?.machineResolved;
  const account = ticket?.accountResolved;

  const [logOpen, setLogOpen] = useState(false);

  const handleBack = () => {
    window.history.back();
  };

  if (loading) {
    return html`
      <section class="container mx-auto py-8 px-4">
        <div class="card max-w-xl mx-auto">
          <div class="card-body flex flex-col items-center justify-center min-h-[200px]">
            <span class="loading loading-spinner loading-lg text-primary mb-4"></span>
            <span class="text-base-content/70">Lade Ticketdaten ...</span>
          </div>
        </div>
      </section>
    `;
  }

  if (!ticket) {
    return html`
      <section class="container mx-auto py-8 px-4">
        <div class="card max-w-xl mx-auto">
          <div class="card-body">
            <div class="alert alert-error mb-4">
              <span>Ticket ${ticketID} nicht gefunden</span>
            </div>
            <button class="btn btn-ghost" @click=${handleBack}>
              <svg
                xmlns="http://www.w3.org/2000/svg"
                class="inline w-5 h-5 mr-1"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                stroke-width="2"
              >
                <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
              </svg>
              Zurück
            </button>
          </div>
        </div>
      </section>
    `;
  }

  console.log('ticket', ticket);

  return html`
    <section class="container mx-auto py-8 px-4">
      <div class="card max-w-xl mx-auto" tabindex="0">
        <div class="card-body">
          <button class="btn btn-ghost mb-4" @click=${handleBack}>
            <svg
              xmlns="http://www.w3.org/2000/svg"
              class="inline w-5 h-5 mr-1"
              fill="none"
              viewBox="0 0 24 24"
              stroke="currentColor"
              stroke-width="2"
            >
              <path stroke-linecap="round" stroke-linejoin="round" d="M15 19l-7-7 7-7" />
            </svg>
            Zurück
          </button>
          <h2 class="card-title text-2xl font-bold text-primary mb-6">Ticket Details</h2>
          <div class="mb-6 space-y-2">
            <div class="flex justify-between">
              <span class="font-semibold">Ticket ID:</span>
              <span>${ticket.caseNumber}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Telefonnummer:</span>
              <span>${ticket.phoneNumber}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Erstellt am:</span>
              <span>${format(ticket.createdOn, 'dd.MM.yyyy HH:mm')}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Name:</span>
              <span>${ticket.contact}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Knowledge Queue:</span>
              <span>${ticket.knowledgeQueue ?? '-'}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Status:</span>
              <span>${ticket.status}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Priorität:</span>
              <span>${ticket.priority}</span>
            </div>
            <div class="flex justify-between">
              <span class="font-semibold">Problem:</span>
              <span>${ticket.description}</span>
            </div>
            ${ticket.escalated === 'Yes'
              ? html`
                  <div class="flex justify-between">
                    <span class="font-semibold">Eskaliert:</span>
                    <span>Ja</span>
                  </div>
                  <div class="flex justify-between">
                    <span class="font-semibold">Eskaliert am:</span>
                    <span>${format(ticket.escalatedOn, 'dd.MM.yyyy HH:mm')}</span>
                  </div>
                `
              : nothing}
          </div>

          <!-- Machine Details -->
          ${machine
            ? html`
                <div class="mb-4">
                  <div class="font-bold text-accent mb-1">Maschinen Details</div>
                  <div class="grid grid-cols-2 gap-x-4 gap-y-1">
                    <span class="text-base-content/70">Name:</span>
                    <span>${machine.machineName}</span>
                    <span class="text-base-content/70">Seriennummer:</span>
                    <span>${machine.serialNumber}</span>
                    <span class="text-base-content/70">Typ:</span>
                    <span>${machine.machineType}</span>
                    <span class="text-base-content/70">Dossier Nr.:</span>
                    <span>${machine.dossierNumber}</span>
                    <span class="text-base-content/70">Hersteller:</span>
                    <span>${machine.manufacturer}</span>
                    <span class="text-base-content/70">Produktgruppe:</span>
                    <span>${machine.productGroup}</span>
                  </div>
                </div>
              `
            : html`<div class="mb-4 text-warning">Keine Maschine verknüpft</div>`}

          <!-- Account Details -->
          ${account
            ? html`
                <div class="mb-4">
                  <div class="font-bold text-accent mb-1">Account Details</div>
                  <div class="grid grid-cols-2 gap-x-4 gap-y-1">
                    <span class="text-base-content/70">Name:</span>
                    <span>${account.accountName}</span>
                    <span class="text-base-content/70">Nummer:</span>
                    <span>${account.accountNumber}</span>
                    <span class="text-base-content/70">Adresse:</span>
                    <span>${account.address1Street1}, ${account.address1ZipPostalCode} ${account.address1City}</span>
                    <span class="text-base-content/70">Land:</span>
                    <span>${account.address1Country}</span>
                    <span class="text-base-content/70">Branche:</span>
                    <span>${account.industry}</span>
                  </div>
                </div>
              `
            : html`<div class="mb-4 text-warning">Kein Account verknüpft</div>`}

          <!-- Vapi Call Log Section -->
          <div class="mt-8">
            <div class="mb-2 sticky top-0 bg-primary-content">
              <button
                class="w-full flex items-center justify-between py-2 px-0 text-left text-base-content/60 hover:bg-base-200/40 focus:outline-none transition-colors rounded"
                @click=${() => setLogOpen(!logOpen)}
                aria-expanded="${logOpen}"
              >
                <span class="text-sm font-bold">Call Log</span>
                <svg
                  class="w-4 h-4 ml-2 transition-transform ${logOpen ? 'rotate-90' : ''}"
                  fill="none"
                  stroke="currentColor"
                  viewBox="0 0 24 24"
                >
                  <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5l7 7-7 7" />
                </svg>
              </button>
            </div>
            ${logOpen
              ? html`
                  <div class="bg-base-100 border border-base-200 rounded-box p-2 shadow-none">
                    ${ticket.vapiCallLog?.map((entry: string, idx: number) => {
                      let parsed: any = entry;
                      try {
                        parsed = JSON.parse(entry);
                      } catch {}
                      return html`
                        <div
                          class="px-2 py-2 ${idx < (ticket.vapiCallLog?.length ?? 0) - 1
                            ? 'border-b border-base-200'
                            : ''} flex flex-col gap-1"
                        >
                          <span class="text-xs text-base-content/60 mb-1">Eintrag ${idx + 1}</span>
                          <pre
                            class="overflow-x-auto bg-transparent p-0 text-sm text-base-content m-0"
                          ><code class="whitespace-pre-wrap">${typeof parsed === 'object'
                            ? JSON.stringify(parsed, null, 2)
                            : entry}</code></pre>
                        </div>
                      `;
                    })}
                  </div>
                `
              : null}
          </div>
        </div>
      </div>
    </section>
  `;
});
